import {
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { PlatformRequirementIssueStatus } from '@client/graphql/__generated__/types';
import { lowerCase, startCase } from 'lodash';
import { TriangleAlertIcon } from 'lucide-react';
import { FC, MouseEvent } from 'react';
import {
  CompliancePlatformRequirementAttachedDocument,
  CompliancePlatformRequirementIssue,
} from './types';

interface DocumentPlatformRequirementIssuesListProps {
  issues: CompliancePlatformRequirementIssue[];
  onIgnore?: (issue: CompliancePlatformRequirementIssue) => void;
  onUndoIgnore?: (issue: CompliancePlatformRequirementIssue) => void;
  isIgnoring?: boolean;
}

const formatAllowedValue = (value: string): string =>
  value.includes('_') ? startCase(lowerCase(value)) : value;

const getExpectedLabel = (
  issues: CompliancePlatformRequirementIssue[]
): string => {
  const allowedValues = [
    ...new Set(
      issues.flatMap((issue) => [
        ...issue.allowedDocumentCategories,
        ...issue.allowedForms,
      ])
    ),
  ];

  return allowedValues.length > 0
    ? allowedValues.map(formatAllowedValue).join(', ')
    : 'Configured checklist requirement';
};

const getAttachedDocumentsLabel = (count: number): string =>
  count === 1 ? '1 attached doc' : `${count} attached docs`;

const getAttachedDocumentKey = (
  document: CompliancePlatformRequirementAttachedDocument
): string =>
  JSON.stringify({
    ingestionDocumentId: document.ingestionDocumentId,
    platformDocumentId: document.platformDocumentId,
    documentName: document.documentName,
  });

interface ChecklistIssueGroup {
  groupKey: string;
  checklistItemName: string;
  issues: CompliancePlatformRequirementIssue[];
  attachedDocuments: CompliancePlatformRequirementAttachedDocument[];
}

const getIssueGroups = (
  issues: CompliancePlatformRequirementIssue[]
): ChecklistIssueGroup[] => {
  const groupsByKey = new Map<string, ChecklistIssueGroup>();

  for (const issue of issues) {
    const groupKey = issue.groupKey;
    const existing = groupsByKey.get(groupKey);
    const group =
      existing ??
      ({
        groupKey,
        checklistItemName: issue.checklistItemName,
        issues: [],
        attachedDocuments: [],
      } satisfies ChecklistIssueGroup);
    group.issues.push(issue);

    const issueAttachedDocuments =
      issue.allAttachedDocuments.length > 0
        ? issue.allAttachedDocuments
        : issue.attachedDocuments ?? [];
    const attachedDocuments =
      issueAttachedDocuments.length > 0
        ? issueAttachedDocuments
        : [
            {
              documentName: issue.attachedDocumentName,
              platformDocumentId: issue.platformDocumentId,
              isProblemDocument: true,
            },
          ];
    for (const document of attachedDocuments) {
      const existingIndex = group.attachedDocuments.findIndex(
        (candidate) =>
          candidate.ingestionDocumentId === document.ingestionDocumentId &&
          candidate.platformDocumentId === document.platformDocumentId &&
          candidate.documentName === document.documentName
      );
      if (existingIndex === -1) {
        group.attachedDocuments.push(document);
        continue;
      }

      if (document.isProblemDocument) {
        group.attachedDocuments[existingIndex] = {
          ...group.attachedDocuments[existingIndex],
          isProblemDocument: true,
        };
      }
    }
    groupsByKey.set(groupKey, group);
  }

  return [...groupsByKey.values()];
};

/**
 * Right-rail card of SkySlope checklist mismatch blockers for the selected
 * document. These are platform-placement issues, separate from validation
 * suggestions, but use the same ignore/undo affordance.
 */
export const DocumentPlatformRequirementIssuesList: FC<
  DocumentPlatformRequirementIssuesListProps
> = ({ issues, onIgnore, onUndoIgnore, isIgnoring = false }) => {
  if (issues.length === 0) {
    return null;
  }

  const groups = getIssueGroups(issues);
  const heading =
    groups.length === 1
      ? '1 checklist suggestion'
      : `${groups.length} checklist suggestions`;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      p={5}
    >
      <VStack align="stretch" spacing={2}>
        <Heading
          color="compliance.status.warning"
          fontSize="lg"
          fontWeight="normal"
          size="headline"
        >
          {heading}
        </Heading>

        <VStack align="stretch" spacing={1}>
          {groups.map((group) => {
            const openIssues = group.issues.filter(
              (issue) =>
                issue.isAgentActionable &&
                issue.status === PlatformRequirementIssueStatus.OPEN
            );
            const ignoredIssues = group.issues.filter(
              (issue) =>
                issue.isAgentActionable &&
                issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT
            );
            const isResolved = group.issues.every(
              (issue) => issue.status !== PlatformRequirementIssueStatus.OPEN
            );
            const canIgnore = openIssues.length > 0;
            const canUndo = ignoredIssues.length > 0;
            const expectedLabel = getExpectedLabel(group.issues);

            const handleIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                openIssues.forEach((issue) => onIgnore?.(issue));
              }
            };

            const handleUndoIgnore = (event: MouseEvent): void => {
              event.stopPropagation();
              if (!isIgnoring) {
                ignoredIssues.forEach((issue) => onUndoIgnore?.(issue));
              }
            };

            return (
              <Flex
                key={group.groupKey}
                align="flex-start"
                borderRadius="6px"
                gap={2}
                mx={-2}
                opacity={isResolved ? 0.6 : undefined}
                px={2}
                py={1}
              >
                <Icon
                  as={TriangleAlertIcon}
                  boxSize="16px"
                  color={
                    isResolved
                      ? 'compliance.text.muted'
                      : 'compliance.status.warning'
                  }
                  flexShrink={0}
                  mt={1}
                />
                <Flex direction="column" gap={1} minW={0}>
                  <Text
                    color={
                      isResolved
                        ? 'compliance.text.muted'
                        : 'compliance.text.primary'
                    }
                    fontSize="sm"
                    fontWeight="medium"
                    textDecoration={isResolved ? 'line-through' : undefined}
                  >
                    Wrong document attached
                  </Text>
                  <VStack
                    align="stretch"
                    color="whiteAlpha.600"
                    fontSize="sm"
                    spacing={0.5}
                  >
                    <Text color="compliance.text.secondary">
                      {group.checklistItemName}
                    </Text>
                    <Text>
                      {getAttachedDocumentsLabel(
                        group.attachedDocuments.length
                      )}
                    </Text>
                    <VStack align="stretch" spacing={0.5}>
                      {group.attachedDocuments.map((document) => (
                        <Flex
                          key={getAttachedDocumentKey(document)}
                          align="center"
                          gap={1.5}
                          minW={0}
                        >
                          <Text color="whiteAlpha.700" fontSize="sm">
                            {document.documentName}
                          </Text>
                          {document.isProblemDocument && (
                            <Badge
                              borderRadius="4px"
                              colorScheme="orange"
                              flexShrink={0}
                              fontSize="2xs"
                              lineHeight="shorter"
                              px={1}
                            >
                              Mismatch
                            </Badge>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                    <Text>Accepted here: {expectedLabel}</Text>
                    {(canIgnore || canUndo) && isIgnoring && (
                      <Flex align="center" gap={1.5}>
                        <Spinner size="xs" />
                        <Text>Updating</Text>
                      </Flex>
                    )}
                    {canIgnore && !isIgnoring && (
                      <Text
                        _hover={{ color: 'compliance.text.secondary' }}
                        aria-label="Ignore checklist suggestion"
                        as="button"
                        cursor="pointer"
                        textAlign="left"
                        textDecoration="underline"
                        type="button"
                        onClick={handleIgnore}
                      >
                        Ignore
                      </Text>
                    )}
                    {canUndo && !isIgnoring && (
                      <Text
                        _hover={{ color: 'compliance.text.secondary' }}
                        aria-label="Undo ignore checklist suggestion"
                        as="button"
                        cursor="pointer"
                        textAlign="left"
                        textDecoration="underline"
                        type="button"
                        onClick={handleUndoIgnore}
                      >
                        Undo
                      </Text>
                    )}
                  </VStack>
                </Flex>
              </Flex>
            );
          })}
        </VStack>
      </VStack>
    </Box>
  );
};
